import { Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";

import type { DebateSide, EligibilityDebate as EligibilityDebatePayload } from "../../api/use-claim-insights";

/**
 * "Analyse contradictoire" — the eligibility debate (CONTRADICTIONS insight, type 7), one calm card
 * per thesis. Apple-design pass (TECH-705): a hairline material with weight-based hierarchy instead of
 * gradient pills / serif / hover-sweep glow; the inline `sources[0]` refs are stripped to plain prose
 * (the PNR evidence is surfaced as chips). The panel header already carries the verdict + reason, so
 * this focuses on the two arguments; the chosen thesis is tinted and marked "Retenu par l'IA".
 */

const CONTROL_CHARS = /\p{Cc}/gu;
// Inline citation refs the pipeline embeds in prose: "(sources[0])", "(source 1)", "[sources 1, 2]".
const SOURCE_REFS = /\s*[([]?\s*sources?\s*\[?\s*\d+(?:\s*,\s*\d+)*\s*\]?\s*[)\]]?/gi;
const clean = (line?: string | null) => (line || "").replace(CONTROL_CHARS, " ").replace(/\s+/g, " ").trim();
const cleanProse = (text?: string | null) => clean(text).replace(SOURCE_REFS, "").replace(/\s+/g, " ").trim();

type Variant = "eligible" | "notEligible";

const VARIANT = {
  eligible: { label: "Éligible", dot: "bg-emerald-500", chosen: "border-emerald-200 bg-emerald-50/40" },
  notEligible: { label: "Non éligible", dot: "bg-rose-500", chosen: "border-rose-200 bg-rose-50/40" },
} satisfies Record<Variant, { label: string; dot: string; chosen: string }>;

// Per-source-type chip tone (full line revealed on hover).
const SOURCE_TONE: Record<string, string> = {
  pnr: "bg-slate-100 text-slate-600",
  pnr_refined: "bg-cyan-50 text-cyan-700",
  flight_info: "bg-blue-50 text-blue-700",
  deterministic: "bg-indigo-50 text-indigo-700",
  analysis: "bg-purple-50 text-purple-700",
};

interface EligibilityDebateProps {
  debate?: EligibilityDebatePayload | null;
}

export function EligibilityDebate({ debate }: EligibilityDebateProps) {
  if (!debate) {
    return null;
  }

  const verdict = (debate.mostProbableVerdict ?? "").toUpperCase();
  const chosenVariant: Variant | null =
    verdict.includes("NOT") || verdict.includes("INELIG")
      ? "notEligible"
      : verdict.includes("ELIG")
        ? "eligible"
        : null;

  const build = (side: DebateSide | undefined, variant: Variant) => {
    // A side can be present but empty (no contradiction on that thesis) — skip it.
    if (!side || (!side.title?.trim() && !side.message?.trim() && !(side.sources ?? []).length)) {
      return null;
    }
    return {
      variant,
      title: clean(side.title),
      message: cleanProse(side.message),
      // Appui shows only PNR-typed sources (the GDS evidence the jurists read).
      appui: (side.sources ?? []).filter((source) => source.type === "pnr"),
      chosen: variant === chosenVariant,
    };
  };
  const sides = [build(debate.eligible, "eligible"), build(debate.notEligible, "notEligible")].filter(
    (side): side is NonNullable<typeof side> => side !== null,
  );

  if (sides.length === 0) {
    return null;
  }

  return (
    <div className={cn("grid gap-3", sides.length > 1 ? "sm:grid-cols-2" : "grid-cols-1")}>
      {sides.map((side) => (
        <article
          key={side.variant}
          className={cn(
            "flex flex-col rounded-2xl border bg-white p-4 shadow-sm",
            side.chosen ? VARIANT[side.variant].chosen : "border-slate-200",
          )}
        >
          {/* verdict label + (when the AI kept this thesis) a calm badge */}
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <span className={cn("h-1.5 w-1.5 rounded-full", VARIANT[side.variant].dot)} />
              {VARIANT[side.variant].label}
            </span>
            {side.chosen ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400">
                <Sparkles className="h-3 w-3" /> Retenu par l'IA
              </span>
            ) : null}
          </div>

          {side.title ? (
            <h4 className="mt-2.5 text-sm font-semibold leading-snug tracking-tight text-slate-900">
              {side.title}
            </h4>
          ) : null}

          {side.message ? (
            <p className="mt-1.5 text-pretty text-sm leading-relaxed text-slate-600">{side.message}</p>
          ) : null}

          {/* appui: PNR source chips only, full line on hover */}
          {side.appui.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-3">
              <span className="text-xs font-medium text-slate-400">Appui</span>
              {side.appui.map((source, index) => (
                <span
                  // biome-ignore lint/suspicious/noArrayIndexKey: static source list
                  key={index}
                  title={clean(source.line)}
                  className={cn(
                    "cursor-help rounded px-1.5 py-0.5 font-mono text-xs",
                    SOURCE_TONE[source.type] || "bg-slate-100 text-slate-600",
                  )}
                >
                  {source.type}
                </span>
              ))}
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
}
